package com.appcreator.client.firebase.functions

import androidx.compose.runtime.ProvidedValue
import com.appcreator.blueprint.core.EnvStore
import com.appcreator.blueprint.firebase.functions.FirebaseFunctionsLoaderSpec
import com.appcreator.compose.api.JsonFetcher.Companion.extract
import com.appcreator.compose.extensions.JSEvaluationConfig
import com.appcreator.compose.loaders.Loader
import kotlinx.datetime.Instant
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonArray
import kotlinx.serialization.json.buildJsonObject

class FunctionsLoader(
    private val region: String,
    private val jsEvaluationConfig: JSEvaluationConfig,
    private val spec: FirebaseFunctionsLoaderSpec
): Loader {

    override suspend fun load(
        envStore: EnvStore,
        mapping: Map<String, String>,
        setPageLastUpdated: ((Instant) -> Unit)?
    ): Pair<ProvidedValue<*>?, Map<String, Any>> {

        val defaults = spec.parameterSettings?.map { (key, value) -> key to value.default }?.toMap()?: emptyMap()
        val queryParameters = spec.queryParameters?.map { (key, value) -> key to envStore.injectValueWithMapping(value, mapping, defaults) }?.toMap()?: emptyMap()

        val raw = load(
            function = spec.name ?: throw IllegalStateException("No function name"),
            region = region,
            queryParameters = queryParameters
        )

        val json = jsonObject(raw as Map<String, *>)

        val properties = mutableMapOf<String, Any>()
        spec.dataShape?.let {
            val inputParameters = spec.parameters.associateWith { key -> envStore.injectValueWithMapping("{$key}", mapping, defaults) }
            extract(
                jsEvaluationConfig = jsEvaluationConfig,
                parameters = inputParameters,
                specs = it.options,
                from = json,
                into = properties
            )
        }
        return null to properties

    }

    override fun canLoadMore(data: Map<String, Any>): Boolean = false
}

internal expect suspend fun load(function: String, region: String, queryParameters: Map<String, String>): Any

private fun jsonObject(map: Map<String, *>): JsonObject {
    return buildJsonObject {
        map.forEach { entry ->
            entry.value?.let {
                put(entry.key, createJsonElement(it as Any))
            }
        }
    }
}

private fun createJsonElement(value: Any?): JsonElement {
    return when(value) {
        is Map<*, *> -> jsonObject(value as Map<String, *>)
        is List<*> -> buildJsonArray {
            value.forEach {
                add(createJsonElement(it as Any))
            }
        }
        else -> JsonPrimitive(value.toString())
    }
}